/**
 * Updates paths of child contents based on parent path array.
 * @param {Array} contents - List of contents to update paths for.
 * @param {Array} parentPathArr - Array representing parent path segments.
 * @returns {Array} - Updated contents with modified paths.
 */
const updateChildPaths = (contents, parentPathArr) => {
  return contents?.map((content) => {
    const contentPathArr = content?.path?.split('/');
    const contentPathSource = contentPathArr[contentPathArr?.length - 1];
    return { ...content, path: [...parentPathArr, contentPathSource].join('/') };
  });
};

/**
 * Resolves a content's path dynamically using a specified key.
 * @param {Object} content - Content object with a path and specified key.
 * @param {string} contentKey - Key to use for resolving path (default: 'title').
 * @returns {Object} - Resolved content object.
 */
const resolveContentWithKey = (content, contentKey = 'title') => {
  const { path, ...contentData } = content;
  const resolvedPath = path?.replace(contentKey, contentData[contentKey]);
  return { ...contentData, path: resolvedPath };
};

const contentIsEmpty = (content) => {
  const isEmpty = !content || !Array.isArray(content) || content.length === 0;
  return isEmpty;
};

/**
 * Builds a hierarchical content tree from a given structure.
 * @param {Array} tree - Existing tree structure (initially empty).
 * @param {Object} contentStructure - Structure containing folders and files.
 * @param {boolean} resolveSource - Whether to resolve paths using content keys.
 * @returns {Array} - Updated content tree structure.
 */
const buildContentTree = (tree = [], contentStructure, resolveSource = true) => {
  const { children, folders, files } = contentStructure;
  const _folders = children || folders;

  if (contentIsEmpty(_folders) && contentIsEmpty(files)) {
    return tree;
  }

  const resolvedContents = resolveSource ? _folders?.map((content) => resolveContentWithKey(content)) : folders;
  const resolvedFiles = resolveSource ? files?.map((file) => resolveContentWithKey(file)) : files;

  const sourcePathKey =
    resolvedContents?.length > 0 ? resolvedContents[0]?.path?.split('/')[1] : resolvedFiles[0]?.path?.split('/')[1];

  if (tree.length === 0) return [...resolvedContents, ...resolvedFiles];

  const resolvedTree = tree?.map((treeContent) => {
    const treeContentPathArr = treeContent?.path?.split('/');
    const treeContentPathKey = treeContentPathArr[treeContentPathArr?.length - 1];

    if (treeContentPathKey === sourcePathKey) {
      return {
        ...treeContent,
        children: [...updateChildPaths([...resolvedContents, ...resolvedFiles], treeContentPathArr)],
      };
    } else if ('children' in treeContent) {
      return {
        ...treeContent,
        children: buildContentTree(treeContent?.children, { folders: resolvedContents, files: resolvedFiles }, false),
      };
    }
    return treeContent;
  });

  return resolvedTree;
};

module.exports = buildContentTree;
